import { useState } from "react";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { COMPANY, SERVICES } from "../data/content";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    // UI-only — no backend wiring yet
    setSubmitted(true);
    toast.success("Thanks — we'll be in touch within one business day.");
    setForm({ name: "", email: "", phone: "", service: "", message: "" });
  };

  return (
    <div data-testid="page-contact">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <section className="bg-[var(--navy)] text-white pt-36 pb-20 md:pb-28">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="eyebrow eyebrow-line mb-4">Contact</div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] tracking-tight">
              Let's Discuss
              <br />
              <span className="italic font-light text-white/80">Your Project.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              Tell us what's going on — a leak, a remodel, a roof on its last
              winter. We'll get back within one business day with next steps.
            </p>
          </div>
        </div>
      </section>

      {/* Split layout */}
      <section className="bg-[var(--bg-light)]">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[var(--border-light)] my-12 md:my-20 bg-white">
          {/* Left info column */}
          <aside className="lg:col-span-5 bg-[var(--navy)] text-white p-10 md:p-14 flex flex-col">
            <div className="eyebrow eyebrow-line text-[var(--copper)] mb-4">Reach Us</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight mb-10">
              Talk to a real human — usually the same day.
            </h2>

            <div className="space-y-7 text-sm text-white/90">
              <div className="flex items-start gap-4">
                <Phone size={18} className="text-[var(--copper)] mt-1 shrink-0" />
                <div>
                  <div className="eyebrow mb-1">Phone</div>
                  <a
                    href={`tel:${COMPANY.phoneTel}`}
                    data-testid="contact-phone"
                    className="font-serif-display text-2xl hover:text-[var(--copper)] transition-colors"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail size={18} className="text-[var(--copper)] mt-1 shrink-0" />
                <div>
                  <div className="eyebrow mb-1">Email</div>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email"
                    className="hover:text-[var(--copper)] transition-colors"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-[var(--copper)] mt-1 shrink-0" />
                <div>
                  <div className="eyebrow mb-1">Office</div>
                  <div data-testid="contact-address">{COMPANY.address}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock size={18} className="text-[var(--copper)] mt-1 shrink-0" />
                <div className="w-full">
                  <div className="eyebrow mb-2">Hours</div>
                  <ul className="space-y-1.5 text-white/80">
                    {COMPANY.hours.map((h) => (
                      <li key={h.day} className="flex justify-between gap-6">
                        <span>{h.day}</span>
                        <span className="text-white/60">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-10 border-t border-white/10">
              <div className="eyebrow mb-2">Service Area</div>
              <p className="text-white/70 text-sm leading-relaxed">
                Braintree, Weymouth, Quincy, Milton, Hingham, Randolph, Holbrook,
                Bridgewater and surrounding South Shore communities.
              </p>
            </div>
          </aside>

          {/* Right form column */}
          <div className="lg:col-span-7 p-10 md:p-14">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="h-full min-h-[480px] flex flex-col items-center justify-center text-center max-w-md mx-auto"
              >
                <CheckCircle2 size={56} className="text-[var(--copper)] mb-6" />
                <h3 className="font-serif-display text-3xl md:text-4xl text-[var(--navy)] mb-4">
                  Thanks — message received.
                </h3>
                <p className="text-[var(--text-mute)] leading-relaxed mb-8">
                  One of our project managers will reach out within one business
                  day. If it's urgent, please give us a call at {COMPANY.phone}.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                  className="btn-outline-navy"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" noValidate className="flex flex-col gap-7">
                <div className="eyebrow eyebrow-line mb-2">Request a Free Estimate</div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-[var(--navy)] leading-tight">
                  Send us a note.
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                  <div>
                    <label className="form-label" htmlFor="name">Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="form-input"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="email">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@email.com"
                      className="form-input"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label" htmlFor="phone">Phone</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(781) 706-2132"
                      className="form-input"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="service">Service Interest</label>
                    <select
                      id="service"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="form-input bg-transparent"
                      data-testid="contact-input-service"
                    >
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Other">Something else</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="form-label" htmlFor="message">Tell us about the project</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={handleChange}
                    placeholder="A few details about your home, the issue, or what you're hoping to do…"
                    className="form-input resize-none"
                    data-testid="contact-input-message"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-6 pt-2">
                  <button type="submit" className="btn-copper" data-testid="contact-submit">
                    Send Message <ArrowUpRight size={16} />
                  </button>
                  <span className="text-xs text-[var(--text-mute)] max-w-xs">
                    Estimates are always free. We respond within one business day.
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map placeholder band */}
      <section className="bg-white border-t border-[var(--border-light)]">
        <div className="container-x py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="eyebrow eyebrow-line mb-3">Find Us</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[var(--navy)] leading-tight mb-4">
              Based in Braintree. Serving the entire South Shore.
            </h2>
            <p className="text-[var(--text-mute)] leading-relaxed">
              Our office is on Central Ave in Braintree — just off Route 3. Stop
              by, or just call. Either works.
            </p>
          </div>
          <div className="md:col-span-7">
            <div
              data-testid="contact-map"
              className="aspect-[16/9] w-full border border-[var(--border-light)] overflow-hidden"
            >
              <iframe
                title="Admiral Roofing Braintree MA"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-71.020%2C42.195%2C-70.985%2C42.220&layer=mapnik&marker=42.2078%2C-71.0040"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
